import { useState } from 'react';
import { Link } from 'react-router-dom';

type Eshop = 'cz' | 'sk';

type Pravidlo = {
  nazev: string;
  zdroj: string;
  cil: string;
  podminky: string[];
  poznamka?: string;
};

/**
 * Pravidla podle skutečného chování `sortOrdersIntoTerms`,
 * `processPreordersToProcessing`, `zaraditNaZakazkuObjednavky` a
 * `zpracovatExpedici` (kroky expedice, vč. Zásilkovny)
 * (apps/web/api/lib/predobjednavky.ts). Když se tam něco změní, změnit i tady.
 */
const PRAVIDLA: Record<Eshop, Pravidlo[]> = {
  cz: [
    {
      nazev: 'Zařazení do termínu',
      zdroj: 'Nevyřízená (jen dobírka) · ERP - vytvoř fakturu (mail)',
      cil: 'Došíváme - odeslat [nejbližší termín]',
      podminky: [
        'Aspoň jedna položka je u nás vedená ve výrobě (vyroba_stock_movements) na termín, který ještě nedošel.',
        'Bere se nejpozdější termín ze všech položek objednávky — odejde až s poslední dávkou.',
        'Objednávka s položkou na zakázku se přeskočí (viz „Ušijeme…" níž).',
      ],
    },
    {
      nazev: 'Vypuštění hotových',
      zdroj: 'Došíváme - odeslat [termín]',
      cil: 'Vyřizuje se',
      podminky: ['Na ten termín (ani na žádný dřívější) už nic nezbývá ve výrobě.'],
    },
    {
      nazev: 'Rovnou do expedice',
      zdroj: 'Nevyřízená (jen dobírka) · ERP - vytvoř fakturu (mail)',
      cil: 'Vyřizuje se',
      podminky: [
        'Nic z objednávky není u nás vedené ve výrobě ani na zakázku.',
        'Navíc: aktuální skladovost všech položek v naší kopii feedu je kladná.',
      ],
      poznamka:
        'Nahrazuje bývalé externí pravidlo v Shoptetu („Dostupnost při vytvoření: Skladem") — to kontrolovalo surové pole dostupnosti, které u režimu na zakázku schválně necháváme na „Skladem", takže omylem posílalo do expedice i objednávky, které měly jít do „Ušijeme…". Zavedeno 9. 8. 2026, obě pravidla v Shoptetu vypnuta.',
    },
    {
      nazev: 'Zboží na zakázku',
      zdroj: 'Nevyřízená (jen dobírka) · ERP - vytvoř fakturu (mail)',
      cil: 'Ušijeme pro Vás do 7 / 10 / 14 dní',
      podminky: [
        'Aspoň jedna položka byla v okamžiku vzniku objednávky u nás aktivně nabízená na zakázku.',
        'Lhůta se bere podle nejdelší z položek — objednávka odejde s tou nejpomalejší.',
      ],
    },
    {
      nazev: 'Zásilkovna do vlastní fronty',
      zdroj: 'Vyřizuje se',
      cil: 'Zásilkovna - send',
      podminky: [
        'Doprava přes Zásilkovnu (výdejní místo, Z-BOX i doručení na adresu).',
        'Stav „Vyřizuje se (PRIORITA)" se nebere.',
        'E-mail / SMS se posílá podle nastavení stavu „Zásilkovna - send" v Shoptetu.',
      ],
      poznamka: 'Zavedeno 2. 11. 2026. POZASTAVENO od 3. 11. 2026 — automat ho teď nepouští.',
    },
  ],
  sk: [
    {
      nazev: 'Zařazení do termínu',
      zdroj: 'Nevyřízená (jen dobírka) · Brani - vytvoř fakturu (mail)',
      cil: 'Došíváme - odeslat [nejbližší termín]',
      podminky: [
        'Stejná evidence výroby jako CZ — výroba je jedna, společná pro oba e-shopy.',
        'Termínové stavy se na SK jmenují česky, přebírají se beze změny.',
        'Objednávka s položkou na zakázku se přeskočí.',
      ],
    },
    {
      nazev: 'Vypuštění hotových',
      zdroj: 'Došíváme - odeslat [termín]',
      cil: 'Vyřizuje se',
      podminky: ['Na ten termín (ani na žádný dřívější) už nic nezbývá ve výrobě.'],
    },
    {
      nazev: 'Rovnou do expedice',
      zdroj: 'Nevyřízená (jen dobírka) · Brani - vytvoř fakturu (mail)',
      cil: 'Vyřizuje se',
      podminky: ['Nic z objednávky není u nás vedené ve výrobě ani na zakázku.'],
      poznamka:
        'SK zatím nemá vlastní feed skladovosti, takže tu chybí ta druhá pojistka, co má CZ (kontrola aktuální skladovosti). Bere se jen to, že o žádném problému nevíme.',
    },
    {
      nazev: 'Zboží na zakázku',
      zdroj: 'Nevyřízená (jen dobírka) · Brani - vytvoř fakturu (mail)',
      cil: 'Ušijeme pre vás do 7 / 10 / 14 dní',
      podminky: [
        'Stejná logika jako CZ — jen slovenské znění stavů a slovenská ID dostupností (ID se mezi e-shopy nesdílí).',
      ],
      poznamka:
        'Do 8. 8. 2026 tohle na SK nezajišťoval nikdo — objednávky se zbožím na zakázku prostě zůstávaly viset v „Nevyřízená".',
    },
    {
      nazev: 'Vyzvednutá dobírka',
      zdroj: 'Brani - vyzvednutá dobírka',
      cil: 'Brani - vytvoř fakturu (no mail)',
      podminky: [
        'Platba dobírkou (Dobierkou (MO)).',
        'E-mail / SMS se posílá podle nastavení cílového stavu v Shoptetu.',
      ],
      poznamka:
        'Převzato z pravidla v Shoptetu (11. 10. 2026). Ve stejném běhu ji hned převezme další pravidlo „Předej k fakturaci".',
    },
    {
      nazev: 'Expedujeme → Odesláno',
      zdroj: 'Expedujeme',
      cil: 'Odesláno',
      podminky: [
        'Doprava přes dopravce: Packeta, DPD nebo GLS (adresa i výdejní místo).',
        'Objednávka je v „Expedujeme" aspoň 5 minut — Brani stihne doplnit číslo zásilky.',
        'E-mail / SMS se posílá podle nastavení stavu „Odesláno" v Shoptetu.',
      ],
      poznamka: 'Převzato z pravidla v Shoptetu (11. 10. 2026).',
    },
    {
      nazev: 'Předej k fakturaci (hotovo)',
      zdroj: 'Brani - vytvoř fakturu (no mail)',
      cil: 'Vyřízena',
      podminky: [
        'Bez podmínky — přepne se každá objednávka v tomto stavu.',
        'Potichu: zákazníkovi neodejde e-mail ani SMS.',
      ],
      poznamka:
        'Převzato z pravidla v Shoptetu (11. 10. 2026). To lze v Shoptetu vypnout — běží v našem automatu každých 5 minut.',
    },
    {
      nazev: 'Zásilkovna do vlastní fronty',
      zdroj: 'Vyřizuje se',
      cil: 'Zásilkovna - send',
      podminky: [
        'Doprava přes Packetu (Zásilkovnu) (výdejní místo, Z-BOX i doručení na adresu).',
        'Stav „Vyřizuje se (PRIORITA)" se nebere.',
        'E-mail / SMS se posílá podle nastavení stavu „Zásilkovna - send" v Shoptetu.',
      ],
      poznamka: 'Zavedeno 2. 11. 2026. POZASTAVENO od 3. 11. 2026 — automat ho teď nepouští.',
    },
  ],
};

/**
 * Přehled pravidel, podle kterých automat každých 5 minut přepíná stavy
 * objednávek — na CZ i SK. Čistě informativní stránka; pravidla sama se
 * mění jen úpravou kódu (apps/web/api/lib/predobjednavky.ts), ne odsud.
 */
export function AutomatizaceObjednavekPage() {
  const [eshop, setEshop] = useState<Eshop>('cz');

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">Automatizace přepínání stavů objednávek</h1>
        <p className="text-sm text-text-secondary mt-1 max-w-3xl">
          Co automaticky přepíná stavy objednávek v Shoptetu — běží každých 5 minut (cron worker),
          pro oba e-shopy zvlášť. Pravidla jsou napsaná v kódu, tahle stránka jen ukazuje, co dnes
          reálně jede.
        </p>
        <p className="text-xs text-text-muted mt-2">
          Živý průběh a historii najdete v{' '}
          <Link to="/vyroba/log-objednavek" className="text-primary hover:underline">
            Logu automatizace objednávek
          </Link>{' '}
          a u{' '}
          <Link to="/vyroba/striharna/na-zakazku" className="text-primary hover:underline">
            Šití na zakázku
          </Link>
          .
        </p>
      </div>

      <div className="flex gap-1.5 mb-5 border-b border-border">
        {(['cz', 'sk'] as const).map((e) => (
          <button
            key={e}
            type="button"
            onClick={() => setEshop(e)}
            className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg cursor-pointer transition-colors -mb-px border-b-2 ${
              eshop === e
                ? 'border-primary text-primary'
                : 'border-transparent text-text-secondary hover:text-text'
            }`}
          >
            {e === 'cz' ? 'V-Mart.cz' : 'V-Mart.sk'}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {PRAVIDLA[eshop].map((p) => (
          <PravidloCard key={p.nazev} pravidlo={p} />
        ))}
      </div>

      <p className="text-xs text-text-muted mt-6 max-w-3xl">
        Přibude další pravidlo? Doplní se do kódu (
        <span className="font-mono">apps/web/api/lib/predobjednavky.ts</span>) a sem se přidá další
        karta — stránka se neřídí žádným nastavením v databázi.
      </p>
    </>
  );
}

function PravidloCard({ pravidlo }: { pravidlo: Pravidlo }) {
  return (
    <div className="bg-surface rounded-xl shadow-card px-5 py-4">
      <h2 className="text-base font-bold text-text mb-3">{pravidlo.nazev}</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        <div>
          <div className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">
            Akce
          </div>
          <div className="text-sm text-text-secondary">
            Objednávky ze stavů: <span className="font-medium text-text">{pravidlo.zdroj}</span>
          </div>
          <div className="text-sm text-text-secondary mt-0.5">
            Přepnout do stavu: <span className="font-medium text-text">{pravidlo.cil}</span>
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">
            Podmínky
          </div>
          <ul className="text-sm text-text-secondary space-y-0.5 list-disc list-inside">
            {pravidlo.podminky.map((cond) => (
              <li key={cond}>{cond}</li>
            ))}
          </ul>
        </div>
      </div>
      {pravidlo.poznamka && (
        <p className="text-xs text-text-muted mt-3 pt-3 border-t border-border-light">
          {pravidlo.poznamka}
        </p>
      )}
    </div>
  );
}
